import React from 'react'
import { Alert, Button, Paper, Stack, Typography } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { usePageTitle } from '../components/common/PageTitleContext'
import { acceptConsolidation } from '../api/consolidations'

type NoticeState = {
  type: 'success' | 'error' | 'info'
  message: string
}

const ConsolidationAcceptPage: React.FC = () => {
  usePageTitle('Confirm subscription switch', { showBreadcrumbs: false })

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') ?? ''
  const [notice, setNotice] = React.useState<NoticeState | null>(null)
  const [submitting, setSubmitting] = React.useState(false)

  const handleAccept = async () => {
    if (!token) {
      setNotice({ type: 'error', message: 'Missing consolidation token.' })
      return
    }

    setSubmitting(true)
    setNotice(null)

    try {
      await acceptConsolidation(token)
      setNotice({
        type: 'success',
        message: 'Your subscription has been switched. The individual plan will cancel at period end.',
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to accept consolidation.'
      setNotice({ type: 'error', message })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: 720 }}>
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h4" component="h1">
            Confirm subscription switch
          </Typography>
          <Typography color="text.secondary">
            Accepting this request moves your access to the station plan. Your personal subscription will cancel at the
            end of the current billing period.
          </Typography>

          {notice ? <Alert severity={notice.type}>{notice.message}</Alert> : null}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Button variant="contained" onClick={handleAccept} disabled={submitting || !token}>
              {submitting ? 'Confirming...' : 'Confirm switch'}
            </Button>
            <Button variant="outlined" onClick={() => navigate('/dashboard')}>
              Back to dashboard
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  )
}

export default ConsolidationAcceptPage
